import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Phone as PhoneIcon, ClockCounterClockwise, Buildings, User, Trash, PencilSimple } from "@phosphor-icons/react";
import ConfirmDialog from "@/components/ConfirmDialog";
import EditChantierDialog from "@/components/EditChantierDialog";
import EditClientDialog from "@/components/EditClientDialog";
import { STATUS_META, CHANTIER_TYPE_META } from "./constants";

const TYPE_META = {
  pro: { label: "Pro", color: "#8B5CF6", Icon: Buildings },
  particulier: { label: "Particulier", color: "#10B981", Icon: User },
};

export default function ChantierHeader({ chantier, client, isAdmin, computedProgress, onStatusChange, onOpenHistory, onChanged }) {
  const nav = useNavigate();
  const meta = STATUS_META[chantier.status] || STATUS_META.waiting;
  const clientType = client?.type || "particulier";
  const tmeta = TYPE_META[clientType] || TYPE_META.particulier;
  const chType = chantier.type ? CHANTIER_TYPE_META[chantier.type] : null;
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editCh, setEditCh] = useState(false);
  const [editCl, setEditCl] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/chantiers/${chantier.id}`);
      toast.success("Chantier supprimé");
      nav("/chantiers");
    } catch (err) { toast.error(formatApiError(err)); setDeleting(false); setConfirmDel(false); }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 p-4 relative overflow-hidden">
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-30" style={{ background: client?.color || meta.color }} />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5 flex-wrap">
              <span>{client?.name || "—"}</span>
              {client && (
                <span
                  className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
                  style={{ background: `${tmeta.color}22`, color: tmeta.color }}
                  data-testid="client-type-badge"
                >
                  <tmeta.Icon size={9} weight="fill" /> {tmeta.label}
                </span>
              )}
              {chType && (
                <span
                  className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
                  style={{ background: `${chType.color}22`, color: chType.color }}
                  data-testid="chantier-type-badge"
                >
                  <span className="text-[10px]">{chType.icon}</span> {chType.label}
                </span>
              )}
              {isAdmin && client && (
                <button
                  onClick={() => setEditCl(true)}
                  className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-[#005CFF]/40 transition-colors"
                  data-testid="edit-client-btn"
                  title="Modifier le client"
                >
                  <PencilSimple size={9} weight="bold" /> Modifier client
                </button>
              )}
              {client && (
                <button
                  onClick={onOpenHistory}
                  className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-[#005CFF]/40 transition-colors"
                  data-testid="client-history-btn"
                >
                  <ClockCounterClockwise size={10} weight="bold" /> Historique
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <div className="font-display text-2xl font-bold" data-testid="chantier-name">{chantier.name}</div>
              {isAdmin && (
                <button
                  onClick={() => setEditCh(true)}
                  className="w-7 h-7 rounded-full bg-white/5 border border-white/10 hover:bg-[#005CFF]/15 hover:border-[#005CFF]/40 text-white/50 hover:text-white flex items-center justify-center active:scale-95 transition-all"
                  title="Modifier le chantier"
                  data-testid="edit-chantier-btn"
                >
                  <PencilSimple size={11} weight="bold" />
                </button>
              )}
            </div>
            {clientType === "pro" && client?.siret && (
              <div className="text-[10px] text-white/40 font-mono tabular-nums mt-0.5" data-testid="client-siret">
                SIRET {client.siret}
              </div>
            )}
            {chantier.address && (
              <a href={`https://maps.google.com/?q=${encodeURIComponent(chantier.address)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-white/60 mt-2">
                <MapPin size={14} /> {chantier.address}
              </a>
            )}
            {client?.phone && (
              <a href={`tel:${client.phone}`} className="inline-flex items-center gap-1 text-xs text-white/60 mt-1 ml-3">
                <PhoneIcon size={14} /> {client.phone}
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isAdmin ? (
              <Select value={chantier.status} onValueChange={onStatusChange}>
                <SelectTrigger className="w-auto h-8 border-white/10 bg-white/5 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10 text-white">
                  {Object.entries(STATUS_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap" style={{ background: `${meta.color}22`, color: meta.color }}>
                {meta.label}
              </span>
            )}
            {isAdmin && (
              <button
                onClick={() => setConfirmDel(true)}
                className="w-8 h-8 rounded-full bg-red-500/10 border border-red-500/25 hover:bg-red-500/20 text-red-400 flex items-center justify-center active:scale-95 transition-colors"
                title="Supprimer le chantier"
                data-testid="chantier-delete-btn"
              >
                <Trash size={13} weight="duotone" />
              </button>
            )}
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/40 mb-1">
            <span>Avancement</span>
            <span>{computedProgress}%</span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-[width]" style={{ width: `${computedProgress}%`, background: meta.color }} />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDel}
        onOpenChange={setConfirmDel}
        onConfirm={doDelete}
        loading={deleting}
        title="Supprimer ce chantier ?"
        description={`« ${chantier.name} » — toutes les interventions, photos, tâches et rapports liés seront également supprimés. Cette action est irréversible.`}
        confirmLabel="Supprimer définitivement"
        testid="chantier-delete-confirm"
      />

      <EditChantierDialog
        open={editCh}
        onOpenChange={setEditCh}
        chantier={chantier}
        onSaved={() => onChanged?.()}
      />

      <EditClientDialog
        open={editCl}
        onOpenChange={setEditCl}
        client={client}
        onSaved={() => onChanged?.()}
      />
    </div>
  );
}
